import type { CSSProperties } from 'react';
import PhoneIcon from '@pixum/svg-icon/lib/Standard/Phone';
import Text, { TextTag, TEXT_VARIANT } from '@pixum/text';
import type { ContactLinkNonCollapsibleProps } from './types';
import { Label } from '../../../label';
import { LinkWithMasking } from '../../../link';
import { EMPTY_PLACEHOLDER } from '../../../../constants';
import { removeEmptySpaces } from 'utils';

/**
 * Non-collapsible contact section for cart footer
 * Displays customer service information in a simple, always-visible format
 *
 * Matches design:
 * - Title: "Unsere Service-Mitarbeiter sind gerne für dich da"
 * - Opening hours: Single line with | separator
 * - Phone: Button with icon + "TELEFON" + number
 */
export function ContactLinkNonCollapsible(props: ContactLinkNonCollapsibleProps) {
  const { align, direction, flow, contact, type } = props;
  const { weekdayOpening, weekendOpening, constants, phone } = contact;
  const showWeekdayOpening = weekdayOpening && weekdayOpening !== EMPTY_PLACEHOLDER;
  const showWeekendOpening = weekendOpening && weekendOpening !== EMPTY_PLACEHOLDER;
  const customProperties = {
    '--footer-links-list-item-align': align,
    '--footer-links-list-item-direction': direction,
    '--footer-links-list-item-flow': flow
  } as CSSProperties;

  // Concatenate opening hours with | separator (matches design)
  let openingTimes = '';

  if (showWeekdayOpening && showWeekendOpening) {
    openingTimes = `${weekdayOpening} | ${weekendOpening}`;
  } else if (showWeekdayOpening) {
    openingTimes = weekdayOpening;
  } else if (showWeekendOpening) {
    openingTimes = weekendOpening;
  }

  return (
    <section className="footer__contact" id={type} style={customProperties}>
      <Text variant={TEXT_VARIANT.TITLE_3}>{constants.i18n_customer_service_is_there}</Text>
      {openingTimes && (
        <Text variant={TEXT_VARIANT.BODY} tag={TextTag.P}>
          {openingTimes}
        </Text>
      )}
      {phone && (
        <LinkWithMasking className="footer__contact-phone" href={`tel:${removeEmptySpaces(phone)}`} masked={false}>
          <Label icon={<PhoneIcon color="white" />} text={constants.i18n_by_phone} variant={TEXT_VARIANT.OVERLINE} />
          <Text variant={TEXT_VARIANT.SUBHEADLINE} tag={TextTag.SPAN}>
            {phone}
          </Text>
        </LinkWithMasking>
      )}
    </section>
  );
}
